/**
 * Guided-tour journeys — one per role (slice 2, 27 Aug 2026).
 *
 * Copy lives here so wording can change without touching the engine
 * (components/tour/GuidedTour.jsx). Each step:
 *   { target?, fallback?, title, body, surface?, tab?, openMenu?, nextLabel? }
 * `target` / `fallback` are CSS selectors (we use [data-tour="…"] anchors).
 * No target → centered card. Target missing/hidden → fallback; both missing
 * → the step is skipped (e.g. desktop nav links hidden behind the mobile
 * hamburger, where the fallback anchors to the ☰ button instead).
 *
 * W-2 (14 Sept): `surface` ('hub' | 'admin' | 'scan') + `tab` let a tour walk
 * across pages — the engine navigates and resumes. `openMenu` pops the ☰ on
 * phones before spotlighting an in-menu link. The admin tour now goes INTO
 * Club Admin tab by tab and ends on the verification CTA (or, for a verified
 * club, on its live sponsor tools).
 */

/** Route for a tour surface — the engine hops here when a step's surface changes. */
export const surfacePath = (surface, clubSlug) => {
  if (surface === 'admin') return `/${clubSlug}/admin`;
  if (surface === 'scan') return `/${clubSlug}/scan`;
  if (surface === 'wallet') return `/${clubSlug}/wallet`;
  if (surface === 'attribution') return `/${clubSlug}/attribution`;
  if (surface === 'account') return `/${clubSlug}/account`;
  return `/${clubSlug}`;
};

const REPLAY_STEP = {
  title: 'Replay this any time',
  body: 'That\'s the essentials. If you ever want a recap, open the menu under your name (top right, or the ☰ button on your phone) and tap "Take the tour".',
};

const FEEDS_STEP = {
  tab: 'whatson', // Back from the Fixtures steps returns here
  target: '[data-tour="hub-sections"]',
  title: 'What\'s on, and the rest of club life',
  body: 'What\'s on is everything coming up — fixtures, training and events — in one list, day by day. Fixtures, Training, News and Events each have their own tab too. Use the team filter to see just your team.',
};

// WT-24 (16 Oct walk-through): club staff check members' cards at the gate or
// bar from Wallet → Scan cards, and no tour said so. Only people who can scan
// (lib/membershipAccess canScanCards: the club runs cards, and an admin gave
// them the Club staff pill) hear about it; nobody else gets a teaser. The
// admin tour already says where scanning lives (People → Members step).
const SCAN_CARDS_STEP = {
  surface: 'wallet',
  target: '[data-tour="wallet-scan-cards"]',
  title: 'Scan cards: your match-day job',
  body: 'At the gate or the bar, tap Scan cards, choose Gate or Bar, and point your phone at a member\'s club card. It tells you straight away whether the card is valid. At the gate you can also record tickets for non-members, or find a member by name if they\'ve forgotten their phone.',
};

// The way into Wallet for tours that don't already go there (coach, team
// manager). `surface` is the page the tour is on at that point.
const scanCardsDoor = (surface) => ({
  ...(surface ? { surface } : {}),
  target: '[data-tour="nav-wallet"]',
  fallback: '[data-tour="nav-menu"]',
  openMenu: true,
  nextLabel: 'Open Wallet',
  title: 'Club cards on match day',
  body: 'You\'re club staff here, so you can check members\' club cards at the gate or the bar. The card scanner is in Wallet. Let\'s open it.',
});

export const buildTourSteps = ({ role, clubName, isParent, isSupporter = false, verified = false, submitted = false, cardsOn = false, holdsCard = false, kitSwapOn = false, canScan = false }) => {
  const club = clubName || 'your club';

  if (role === 'admin') {
    return [
      {
        title: `Welcome to ${club}'s hub 🎉`,
        body: 'This is what every member sees when they open Marakin — your club\'s fixtures, training, news and events in one place. Here\'s a one-minute tour of how you run it.',
      },
      FEEDS_STEP,
      {
        tab: 'fixtures',
        target: '[data-tour="hub-add-fixture"]',
        fallback: '[data-tour="hub-fixtures-tab"]',
        title: 'Add fixtures & training',
        body: 'You can add them yourself right now — fixtures here, training under the Training tab. Or invite your team managers first and let them run their own teams\' schedules. Both work; most clubs do a bit of each.',
      },
      {
        tab: 'fixtures',
        target: '[data-tour="hub-bulk-fixtures"]',
        fallback: '[data-tour="hub-fixtures-tab"]',
        title: 'Or the whole season in one go',
        body: 'Add many takes a typed list or your fixtures spreadsheet and creates the lot — titles, venues, meet times and respond-by dates fill themselves in, and you check everything before it goes live. Training has its own "Set up the season" button: pick teams, evenings and an end date, done.',
      },
      {
        target: '[data-tour="nav-admin"]',
        fallback: '[data-tour="nav-menu"]',
        openMenu: true,
        nextLabel: 'Take me in',
        title: 'Admin is your engine room',
        body: 'Four sections — People, Club, Sponsors and Reports — and it\'s most of the admin job. Let\'s walk through it.',
      },
      {
        surface: 'admin', tab: 'members',
        target: '#admin-tab-members',
        title: 'People → Members — get people in',
        body: cardsOn
          ? 'Invite by link, one at a time or in bulk from a spreadsheet; set who\'s an admin, team manager, coach, parent or player. Everyone here is a community member. The ones who\'ve paid are club members — mark them with the Club member pill on their row. Whoever works the gate or bar is club staff — flag them with the Club staff pill and they can scan cards from their Wallet. Staff can be community and club members too.'
          : 'Invite by link, one at a time or in bulk from a spreadsheet; set who\'s an admin, team manager, coach, parent or player. Start with your committee and team managers — they can invite their own teams from here.',
      },
      {
        surface: 'admin', tab: 'teams',
        target: '#admin-tab-teams',
        title: 'People → Teams',
        body: 'Every team you added in the wizard is here — rename, add age groups, archive old ones. Fixtures, training and the hub\'s team filter all hang off this list.',
      },
      {
        surface: 'admin', tab: 'settings',
        target: '#admin-tab-settings',
        title: 'Club → Settings',
        body: cardsOn
          ? 'Badge, nickname, the club\'s public details, your home ground — and Membership cards: the season-end date every card runs to, and your gate ticket prices for non-members. Shop and Moderation sit alongside.'
          : 'Badge, nickname, the club\'s public details and your home ground — already set from the address you gave us, so home fixtures get a map and a weather forecast. Correct it here if the pin landed in the wrong place. Shop and Moderation sit alongside.',
      },
      {
        surface: 'admin', tab: 'exports',
        target: '#admin-tab-exports',
        title: 'Reports',
        body: cardsOn
          ? 'One pill per report. Attendance and availability downloads; Membership & gate — club members through the gate, tickets and gate money per home fixture, bar visits per week, totals only, no names; and Sponsorship performance' + (verified ? ', with the live sponsor dashboard one tap away.' : ' (plus the live sponsor dashboard) once you\'re verified.')
          : 'One pill per report. Attendance and availability — who\'s turning up, who never answers — as spreadsheets your committee can actually use, and Sponsorship performance (plus the live sponsor dashboard) once you\'re verified.',
      },
      verified
        ? {
            surface: 'admin', tab: 'sponsors',
            target: '[data-tour="admin-sponsors-live"]',
            fallback: '#admin-tab-sponsors',
            title: 'Sponsors — your tools are live',
            body: `${club} is verified, so this is all switched on: add your sponsors and their member offers, hand each sponsor the scan-at-the-till QR flow, and watch the redemption report show them exactly what backing the club brings through their door.`,
          }
        : {
            surface: 'admin', tab: 'sponsors',
            target: '[data-tour="verification-form"]',
            fallback: '#admin-tab-sponsors',
            title: submitted ? 'Sponsors — nearly there' : 'Sponsors — the money bit',
            body: submitted
              ? `Your verification details are with us. Once ${club} is verified this tab lights up: sponsors, member offers, till-side QR redemption and the numbers that keep sponsors renewing. We'll be in touch, normally within a day or two.`
              : `Marakin brings sponsor money into verified clubs — offers your members redeem by QR, and real numbers for the sponsor. To switch it on for ${club}, complete your verification details right here: your role at the club, plus anything that helps us confirm you. Two minutes, then we're in touch.`,
          },
      { ...REPLAY_STEP, surface: 'admin' },
    ];
  }

  if (role === 'team_manager') {
    return [
      {
        title: `Welcome to ${club} 👋`,
        body: 'You\'re set up as a team manager here. This hub is what all your players and parents see — here\'s the one-minute version of your job in it.',
      },
      FEEDS_STEP,
      {
        tab: 'fixtures',
        target: '[data-tour="hub-add-fixture"]',
        fallback: '[data-tour="hub-fixtures-tab"]',
        title: 'Add your team\'s fixtures & training',
        body: 'Add a fixture and Marakin does the rest — venue lookup, map, weather, and it asks everyone for a Yes or No automatically. Training sessions work the same from the Training tab, including weekly repeats.',
      },
      {
        tab: 'fixtures',
        target: '[data-tour="hub-bulk-fixtures"]',
        fallback: '[data-tour="hub-fixtures-tab"]',
        title: 'Got the season\'s list already?',
        body: 'Add many creates your team\'s whole fixture list from a typed grid or a spreadsheet — you check every row before anything goes live. And "Set up the season" under Training creates the weekly sessions in one go.',
      },
      {
        title: 'See who\'s coming — and who hasn\'t answered',
        body: 'Open any fixture or session to see the Yes list, the No list, and who hasn\'t responded yet. One tap sends the stragglers a nudge, so selection night isn\'t a WhatsApp archaeology dig.',
      },
      {
        target: '[data-tour="nav-admin"]',
        fallback: '[data-tour="nav-menu"]',
        openMenu: true,
        nextLabel: 'Take me in',
        title: 'Your admin door',
        body: 'Admin is where you look after your teams\' people. Let\'s step inside.',
      },
      {
        surface: 'admin', tab: 'members',
        target: '#admin-tab-members',
        title: 'Members — your teams only',
        body: 'Invite players, coaches and parents by link (or in bulk from a spreadsheet) and manage your pending invites. You only see people on the teams you manage.',
      },
      {
        surface: 'admin', tab: 'exports',
        target: '#admin-tab-exports',
        title: 'Reports',
        body: 'Attendance and availability for your teams — who turned up, who never answers — as a spreadsheet.',
      },
      // WT-24: club staff only.
      ...(canScan ? [scanCardsDoor('admin'), SCAN_CARDS_STEP] : []),
      { ...REPLAY_STEP, surface: canScan ? 'wallet' : 'admin' },
    ];
  }

  if (role === 'coach') {
    return [
      {
        title: `Welcome to ${club} 👋`,
        body: 'You\'re set up as a coach here. This hub is where your team\'s schedule and availability live — a quick look around.',
      },
      FEEDS_STEP,
      {
        target: '[data-tour="hub-training-tab"]',
        fallback: '[data-tour="hub-sections"]',
        title: 'Training lives here',
        body: 'Your teams\' sessions, with venue, map and weather. You can add and edit sessions and fixtures for your own teams too.',
      },
      {
        title: 'Availability at a glance',
        body: 'Open any fixture or session to see exactly who\'s said Yes, who\'s said No, and who hasn\'t answered — with a one-tap nudge for the silent ones. Plan sessions on real numbers.',
      },
      // WT-24: club staff only.
      ...(canScan ? [scanCardsDoor(), SCAN_CARDS_STEP, { ...REPLAY_STEP, surface: 'wallet' }] : [REPLAY_STEP]),
    ];
  }

  if (role === 'sponsor') {
    return [
      {
        title: `You're a sponsor of ${club} 🤝`,
        body: 'Marakin turns your sponsorship into visits you can count. Members carry your offer as a QR code; you scan it at the till. Two screens, that\'s the whole app.',
      },
      {
        target: '[data-tour="nav-scan"]',
        fallback: '[data-tour="nav-menu"]',
        title: 'Scan QR — the till screen',
        body: 'A member shows their code, you scan or type it, enter the bill total, and the app shows the discount to apply. Takes seconds.',
      },
      {
        target: '[data-tour="nav-attribution"]',
        fallback: '[data-tour="nav-menu"]',
        openMenu: true,
        nextLabel: 'Show me',
        title: 'Attribution — your numbers',
        body: 'Every redemption is logged. Let\'s look at the report.',
      },
      {
        surface: 'attribution',
        target: '[data-tour="attribution-stats"]',
        title: 'What the sponsorship brings through the door',
        body: 'Visits, spend, discount honoured, average basket — per offer and over time. This is the page to open when renewal comes round.',
      },
      { ...REPLAY_STEP, surface: 'attribution' },
    ];
  }

  // Default: player / parent / supporter
  const steps = [
    {
      title: `Welcome to ${club} 👋`,
      body: 'Everything the club does — fixtures, training, news, events — now lives in one place, so nothing important gets buried in a group chat.',
    },
    FEEDS_STEP,
    // WT-9: supporters have no Going / Not going on team fixtures or training
    // (ClubHub's userCanAttend: players, coaches and TMs of that team), only on
    // club-wide events. So they get what they CAN do instead of the Yes/No step.
    isSupporter && !isParent
      ? {
          target: '[data-tour="hub-fixtures-tab"]',
          fallback: '[data-tour="hub-sections"]',
          title: 'Follow the club from here',
          body: 'Fixtures has every match, with kick-off and venue, plus a map and weather forecast when we have them, so you always know where to be. News has the latest from the club. Each team\'s players answer Going or Not going for their matches; when the whole club is invited, like a social, you can answer too.',
        }
      : {
          target: '[data-tour="hub-whatson-tab"]',
          fallback: '[data-tour="hub-sections"]',
          title: 'The one thing your coach needs: your Yes or No',
          body: 'Open any fixture or training session in What\'s on and tap Going or Not going — it takes two seconds and it\'s how the club plans. You can add a note like "arriving late" too.',
        },
  ];
  if (isParent) {
    steps.push({
      surface: 'account',
      target: '[data-tour="account-family"]',
      title: 'Your family',
      body: 'Add your children here. After that every fixture shows a row per child, so you answer for each of them — and for yourself — in one place.',
    });
  }
  // Wallet: the one place for offers, codes and (for club members) the card.
  // Parents are on the Account page at this point — the navbar is there too,
  // so hop straight on rather than bouncing via the hub (QA r2 #5).
  steps.push({
    ...(isParent ? { surface: 'account' } : {}),
    target: '[data-tour="nav-wallet"]',
    fallback: '[data-tour="nav-menu"]',
    openMenu: true,
    nextLabel: 'Open Wallet',
    title: holdsCard ? 'Your club card and offers live in Wallet' : 'Your sponsor offers live in Wallet',
    body: holdsCard
      ? 'Wallet is where your club card and your sponsor discount codes live. Let\'s open it.'
      : 'Wallet is where the sponsor offers and your discount codes live. Let\'s open it.',
  });
  if (holdsCard) {
    // v35: only club members who hold a live card get this step — community
    // members never hear about cards (no teaser, by design).
    steps.push({
      surface: 'wallet',
      target: '[data-tour="wallet-card"]',
      title: 'Your club card',
      body: `You're a club member of ${club}, so this card is yours — show the QR at the gate for free entry to home games and at the bar for member treatment. It refreshes itself every minute, so a screenshot won't work but the real thing always does.`,
    });
  }
  // WT-24: a member an admin made club staff (gate or bar volunteer).
  if (canScan) steps.push(SCAN_CARDS_STEP);
  steps.push({
    surface: 'wallet',
    target: '[data-tour="wallet-offers"]',
    fallback: '[data-tour="wallet-codes"]',
    title: 'Sponsor offers',
    body: 'Local businesses back the club with real offers for everyone in it. Tap one for your own code, show it at the till, and save money for supporting your club. Codes you\'ve claimed sit under "Your codes".',
  });
  if (kitSwapOn) {
    // B-45: Shop step — only for clubs running Kit Swap (no teaser otherwise).
    steps.push({
      surface: 'wallet',
      target: '[data-tour="nav-shop"]',
      fallback: '[data-tour="nav-menu"]',
      openMenu: true,
      title: 'Shop — Kit Shop and Kit Swap',
      body: `Shop has two tabs. Kit Shop is ${club}'s official kit. Kit Swap is free outgrown kit from other members: tap "I'd like this" and it's left at the club for you to collect — or list something your family has grown out of.`,
    });
  }
  steps.push({ ...REPLAY_STEP, surface: 'wallet' });
  return steps;
};

/** Which tour a membership gets, and the surface it runs on. */
export const resolveTourRole = (membership) => {
  if (!membership) return null;
  if (membership.role === 'sponsor') return 'sponsor';
  if (membership.role === 'admin' || membership.clubAdmin) return 'admin';
  const teamRoles = Array.isArray(membership.teamRoles) ? membership.teamRoles.map(tr => tr.role) : [];
  if (membership.role === 'team_manager' || teamRoles.includes('team_manager')) return 'team_manager';
  if (membership.role === 'coach' || teamRoles.includes('coach')) return 'coach';
  return 'member';
};

/**
 * WT-9: a supporter with no playing place on any team, and no children to
 * answer for. They can't say Going / Not going on team events, so the member
 * tour swaps its Yes/No step for one about what they can do.
 */
export const isSupporterOnly = (membership) => {
  if (!membership || membership.role !== 'supporter' || membership.isGuardian) return false;
  const teamRoles = Array.isArray(membership.teamRoles) ? membership.teamRoles.map(tr => tr.role) : [];
  return !teamRoles.includes('player');
};
